import { Box, FormGroup, Icon, Radio, Text, Tooltip } from '@ksyos/design-system';
import { YesOrNoOrStoppedOrUnknown } from '@common/enums';
import { getValidationErrorTranslation, type ValidationErrors } from '@frontend/utils';
import { NumberInputContext, RadioGroupFormGroup, TextInputFormGroup } from '../../../processActivityFormGroupFields';

type AlcoholFormGroupProps = {
    data: {
        alcoholStoppedSince: string | null;
        daysAWeek: number | null;
        glassesADay: number | null;
        isAlcoholUser: YesOrNoOrStoppedOrUnknown | null;
    };
    errors: ValidationErrors;
    onChange: (data: Partial<AlcoholFormGroupProps['data']>, validateKeys?: string | string[] | undefined) => void;
};

export function AlcoholConsumptionFormGroup(props: Readonly<AlcoholFormGroupProps>): JSX.Element {
    const { data, errors, onChange } = props;

    return (
        <>
            <RadioGroupFormGroup
                error={getValidationErrorTranslation(errors.get('isAlcoholUser'))}
                fieldKey="isAlcoholUser"
                hasErrors={errors.has('isAlcoholUser')}
                label="Alcoholconsumptie"
                onChange={async (value) => {
                    const validateKeys = ['isAlcoholUser'];
                    switch (value) {
                        case YesOrNoOrStoppedOrUnknown.yes:
                            validateKeys.push('alcoholStoppedSince');
                            break;
                        case YesOrNoOrStoppedOrUnknown.stopped:
                            validateKeys.push('daysAWeek', 'glassesADay');
                            break;
                        default:
                            validateKeys.push('daysAWeek', 'glassesADay', 'alcoholStoppedSince');
                            break;
                    }
                    onChange(
                        {
                            isAlcoholUser: (value ?? null) as YesOrNoOrStoppedOrUnknown,
                            ...([YesOrNoOrStoppedOrUnknown.no, YesOrNoOrStoppedOrUnknown.stopped].includes(
                                value as YesOrNoOrStoppedOrUnknown
                            )
                                ? { daysAWeek: null, glassesADay: null }
                                : {}),
                            ...([YesOrNoOrStoppedOrUnknown.no, YesOrNoOrStoppedOrUnknown.yes].includes(
                                value as YesOrNoOrStoppedOrUnknown
                            )
                                ? { alcoholStoppedSince: null }
                                : {}),
                            ...([YesOrNoOrStoppedOrUnknown.unknown].includes(value as YesOrNoOrStoppedOrUnknown)
                                ? {
                                      alcoholStoppedSince: null,
                                      daysAWeek: null,
                                      glassesADay: null,
                                  }
                                : {}),
                        },
                        validateKeys
                    );
                }}
                sx={{ mb: 3 }}
                value={data.isAlcoholUser ?? ''}
            >
                <Radio
                    key={YesOrNoOrStoppedOrUnknown.yes}
                    label={YesOrNoOrStoppedOrUnknown.yes}
                    value={YesOrNoOrStoppedOrUnknown.yes}
                />
                <Radio
                    key={YesOrNoOrStoppedOrUnknown.no}
                    label={YesOrNoOrStoppedOrUnknown.no}
                    value={YesOrNoOrStoppedOrUnknown.no}
                />
                <Radio
                    key={YesOrNoOrStoppedOrUnknown.stopped}
                    label={YesOrNoOrStoppedOrUnknown.stopped}
                    value={YesOrNoOrStoppedOrUnknown.stopped}
                />
                <Radio
                    key={YesOrNoOrStoppedOrUnknown.unknown}
                    label={YesOrNoOrStoppedOrUnknown.unknown}
                    value={YesOrNoOrStoppedOrUnknown.unknown}
                />
            </RadioGroupFormGroup>

            {data.isAlcoholUser === YesOrNoOrStoppedOrUnknown.yes && (
                <FormGroup label="Hoeveelheid">
                    <Box
                        display="grid"
                        gridTemplateColumns={['1fr', 'max-content max-content']}
                        sx={{ columnGap: 8, rowGap: 3 }}
                    >
                        <Box
                            display="grid"
                            gridTemplateColumns="80px max-content"
                        >
                            <NumberInputContext
                                fieldKey={'daysAWeek'}
                                maximumFractionDigits={2}
                            />
                            <Text
                                fontWeight="medium"
                                ml={3}
                                mt={2}
                                size="sm"
                            >
                                dagen per week
                            </Text>
                        </Box>
                        <Box
                            display="grid"
                            gridTemplateColumns="80px max-content"
                        >
                            <NumberInputContext fieldKey={'glassesADay'} />
                            <Box
                                display="flex"
                                gap={2}
                                ml={3}
                                mt={2}
                            >
                                <Text
                                    fontWeight="medium"
                                    size="sm"
                                >
                                    glazen per dag
                                    <Tooltip
                                        content="Op een typische dag van consumptie"
                                        placement="bottom"
                                    >
                                        <Icon
                                            color="primary"
                                            icon="information"
                                            sx={{ ml: 2 }}
                                        />
                                    </Tooltip>
                                </Text>
                            </Box>
                        </Box>
                    </Box>
                </FormGroup>
            )}

            {data.isAlcoholUser === YesOrNoOrStoppedOrUnknown.stopped && (
                <TextInputFormGroup
                    fieldKey="alcoholStoppedSince"
                    label="Gestopt sinds"
                />
            )}
        </>
    );
}
